import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Menu.css';
import { userApi } from '../api';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { Category } from '../types/game';

const Menu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sortedCategories, setSortedCategories] = useState<Category[]>([]);
  const [currentCategory, setCurrentCategory] = useState<Category>({ name: 'Hackers & Threats' , isCompleted: false });
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCategories(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const sortedCategories = [...categories].sort((a, b) => {
      if (a.isCompleted === b.isCompleted) return 0;
      return a.isCompleted ? 1 : -1;
    });
    setSortedCategories(sortedCategories);
  }, [categories]);

  useEffect(() => {
    if (!isOpen) {
      setShowCategories(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await userApi.getAvailableCategories();
        if (response.data.status === 'success') {
          setCategories(response.data.categories);
          if (response.data.categories.length > 0) {
            setCurrentCategory(response.data.categories[0].name);
          }
        }
      } catch (err) {
        console.error('Failed to load categories');
      }
    };
    fetchCategories();
  }, []);

  const handleCategorySelect = async (categoryId: string) => {
    try {
      setCurrentCategory({ name: categoryId, isCompleted: false });
      
      // Update user's category preference
      const response = await userApi.updateCategory(categoryId);
      
      if (response.data.status === 'success') {
        setIsOpen(false);
        setShowCategories(false);
        navigate('/play');
      } else {
        console.error('Failed to start game');
      }
    } catch (error) {
      console.error('Error updating category or starting game:', error);
      setShowCategories(true);
    }
  };

  const toggleCategories = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the menu from closing
    setShowCategories(!showCategories);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="menu-container" ref={menuRef}>
      <button 
        className={`menu-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="burger-icon">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </button>
      
      {isOpen && (
        <div className="menu-items">
          <Link to="/welcome" className="menu-item" onClick={() => setIsOpen(false)}>
            New Game
          </Link>
          <div 
            className="menu-item has-submenu"
            onClick={toggleCategories}
          >
            Change Category
            <div className="category-info">
              <span className="submenu-arrow">{showCategories ? '▼' : '▶'}</span>
            </div>
          </div>
          {showCategories && (
            <div className="submenu-items">
              {sortedCategories.map(category => (
                <div
                  key={category.name}
                  className={`submenu-item ${category.name === currentCategory.name ? 'active' : ''} ${category.isCompleted ? 'completed' : ''}`}
                  onClick={() => !category.isCompleted && handleCategorySelect(category.name)}
                >
                  {category.name}
                  {category.name === currentCategory.name && <span className="selected-mark">✓</span>}
                  {category.isCompleted && <span className="completion-badge">✓</span>}
                </div>
              ))}
            </div>
          )}
          <Link to="/profile" className="menu-item" onClick={() => setIsOpen(false)}>
            Profile
          </Link>
          <Link to="/leaderboard" className="menu-item" onClick={() => setIsOpen(false)}>
            Leaderboard
          </Link>
          <Link to="/study-details" className="menu-item" onClick={() => setIsOpen(false)}>
            Study Details
          </Link>
          <Link to="/privacy-notice" className="menu-item" onClick={() => setIsOpen(false)}>
            Privacy Notice
          </Link>
          {/* <a 
            href="https://portal.onersac.com/your-groups/!hqSDkEQIqhYWwfCvFK:415.onersac.com"
            className="menu-item"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsOpen(false)}
          >
            Join the Group!
          </a> */}
          <a 
            href="https://www.onersac.com/membership"
            className="menu-item"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsOpen(false)}
          >
            About RSAC™ Membership
          </a>
          <a 
            href="https://www.onersac.com/rsac-labs"
            className="menu-item"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsOpen(false)}
          >
            RSAC™ Labs
          </a>
          <a 
            href="https://x.com/share?text=I just tested my security expertise against AI models! Try AI Showdown: https://portal.onersac.com/play-and-learn/haiqu"
            className="menu-item"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsOpen(false)}
          >
            Share on X
          </a>
          <div 
            className="menu-item logout"
            onClick={() => {
              setIsOpen(false);
              handleLogout();
            }}
          >
            Logout
          </div>
        </div>
      )}
    </div>
  );
};

export default Menu; 